import { useState } from "react";
import Image from "next/image";

/**
 * YouTube Facade Component
 * Shows a thumbnail with play button, only loads iframe on click
 * This prevents YouTube from setting cookies until user interaction
 */
export function YouTubeFacade({ src, title = "YouTube video player", className = "" }) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Extract video ID from various YouTube URL formats
  const getVideoId = (url) => {
    if (!url) return null;
    
    // Handle youtube-nocookie.com/embed/VIDEO_ID format
    const embedMatch = url.match(/(?:youtube\.com|youtube-nocookie\.com)\/embed\/([^?&]+)/);
    if (embedMatch) return embedMatch[1];
    
    // Handle youtube.com/watch?v=VIDEO_ID format
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) return watchMatch[1];
    
    // Handle youtu.be/VIDEO_ID format
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return shortMatch[1];
    
    return null;
  };

  const videoId = getVideoId(src);
  const thumbnailUrl = videoId 
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null;

  if (isLoaded) {
    return (
      <iframe
        width="100%"
        height="100%"
        src={`${src}${src.includes('?') ? '&' : '?'}autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={className}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsLoaded(true)}
      className={`relative w-full h-full cursor-pointer group bg-black ${className}`}
      aria-label={`Play video: ${title}`}
    >
      {thumbnailUrl && (
        <Image
          src={thumbnailUrl}
          alt=""
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      )}
      {/* Play button overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:bg-red-700 group-hover:scale-110 transition-all duration-200">
          <svg
            className="w-8 h-8 md:w-10 md:h-10 text-white ml-1"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      {/* Gradient overlay for better contrast */}
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-200" />
    </button>
  );
}

